import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Unlock, CheckCircle2, Lock, ShieldCheck, ShieldAlert, ChevronRight, X, RefreshCcw, ChevronDown, ChevronUp, Clock, KeyRound, Pencil, Trash } from "lucide-react";
import { api, formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/contexts/AuthContext";

const fmt = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const TX_LABEL = {
  cash_expense: "Cash Expense",
  cash_addition: "Cash Added",
  cash_withdrawal: "Owner Cash Removal",
  upi_expense: "UPI Expense",
  upi_purchase: "UPI Purchase",
  upi_transfer: "UPI → Owner",
  upi_addition: "UPI ← Owner",
};

export default function AdminCashHistory() {
  const { isMaster } = useAuth();
  const master = isMaster();
  const [days, setDays] = useState([]);
  const [summary, setSummary] = useState(null);
  const [health, setHealth] = useState(null);
  const [openDay, setOpenDay] = useState(null);
  const [expanded, setExpanded] = useState({}); // date -> bool
  const [overrideMode, setOverrideMode] = useState(false);
  const [showOverridePrompt, setShowOverridePrompt] = useState(false);
  const [editDay, setEditDay] = useState(null); // date string
  const [deletingTx, setDeletingTx] = useState(null); // tx object

  const load = async () => {
    try {
      const [{ data: d }, { data: s }, { data: h }] = await Promise.all([
        api.get("/admin/cash/days?limit=7"),
        api.get("/admin/cash/summary"),
        api.get("/admin/cash/health"),
      ]);
      setDays(d); setSummary(s); setHealth(h);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const reopen = async (date) => {
    if (!window.confirm(`Reopen ${date}?`)) return;
    try { await api.post(`/admin/cash/day/${date}/reopen`); toast.success("Reopened"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const drilldown = async (date) => {
    setOpenDay({ loading: true, date });
    try { const { data } = await api.get(`/admin/cash/day/${date}`); setOpenDay({ loading: false, data }); }
    catch (e) { toast.error(formatApiError(e)); setOpenDay(null); }
  };
  const recompute = async (date) => {
    if (!window.confirm(`Force-refresh opening balances for ${date} from previous day's closing?`)) return;
    try { await api.post(`/admin/cash/day/${date}/carry-forward`); toast.success("Openings re-carried"); load(); if (openDay?.date === date) drilldown(date); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-cash-history" className="pb-16">
      <header className="mb-6 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <Link to="/admin/cash" className="text-xs text-brand-charcoal/60 hover:text-brand-charcoal inline-flex items-center gap-1"><ArrowLeft size={12} /> Back to Today</Link>
          <h1 className="editorial-h1 text-4xl mt-2">Last 7 Days</h1>
        </div>
        <div className="flex gap-2">
          {master && !overrideMode && (
            <button onClick={() => setShowOverridePrompt(true)} className="btn-pill btn-ghost text-xs border-amber-300 text-amber-700" data-testid="btn-override">
              <KeyRound size={11} /> Override
            </button>
          )}
          {master && overrideMode && (
            <span className="btn-pill text-xs bg-amber-100 text-amber-800 border border-amber-300 inline-flex items-center gap-1" data-testid="override-active">
              <KeyRound size={11} /> Override Active
            </span>
          )}
          <button onClick={load} className="btn-pill btn-ghost text-xs"><RefreshCcw size={11} /> Refresh</button>
        </div>
      </header>

      {/* Data Health */}
      {health && (
        <div className={`rounded-2xl border p-5 mb-6 ${health.issues.length === 0 ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`} data-testid="cash-health-strip">
          <div className="flex items-center gap-2 mb-3">
            {health.issues.length === 0 ? <ShieldCheck size={16} className="text-emerald-700" /> : <ShieldAlert size={16} className="text-red-700" />}
            <div className="font-medium">Data Health</div>
            <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ml-2 ${health.issues.length === 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{health.issues.length === 0 ? "All clear" : `${health.issues.length} issue(s)`}</span>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 text-xs">
            <Hp ok={health.days_stored >= 7 || health.days_stored > 0} label={`${health.days_stored} days stored`} />
            <Hp ok={health.cash_chain_ok} label="Cash ledger continuous" />
            <Hp ok={health.upi_chain_ok} label="UPI ledger continuous" />
            <Hp ok={health.all_closings_recorded} label="Prior days closed" />
          </div>
          {health.issues.length > 0 && (
            <ul className="mt-3 text-xs text-red-700 space-y-1">
              {health.issues.map((i, idx) => (
                <li key={idx} data-testid={`health-issue-${idx}`}>🔴 <strong>{i.date}</strong> — {i.type === "cash_carry_forward" ? "Cash" : "UPI"} carry-forward: prev {fmt(i.prev_close)}, today {fmt(i.today_open)} (diff ₹{i.difference.toFixed(2)}).</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Aggregate summary */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <Stat label="Total Billing (7d)" value={fmt(summary.total_billing)} />
          <Stat label="Cash Sales (7d)" value={fmt(summary.total_cash_sales)} />
          <Stat label="UPI Sales (7d)" value={fmt(summary.total_upi_sales)} />
          <Stat label="Cash Difference (7d)" value={fmt(summary.total_cash_difference)} accent={Math.abs(Number(summary.total_cash_difference || 0)) > 0.01} />
        </div>
      )}

      {/* ---------- Compact 7-day table ---------- */}
      <section className="rounded-2xl bg-white border border-brand-line p-5 mb-6" data-testid="ledger-table">
        <div className="text-[11px] uppercase tracking-widest text-brand-charcoal/55 mb-3">Compact 7-day ledger</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[900px]">
            <thead className="text-[9px] uppercase tracking-widest text-brand-charcoal/55">
              <tr>
                <th className="text-left py-2">Date</th>
                <th className="text-right py-2">Open Cash</th>
                <th className="text-right py-2">Cash Sales</th>
                <th className="text-right py-2">Cash Exp</th>
                <th className="text-right py-2">Cash Removed</th>
                <th className="text-right py-2">Close Cash</th>
                <th className="text-right py-2">Open UPI</th>
                <th className="text-right py-2">UPI Sales</th>
                <th className="text-right py-2">UPI Exp</th>
                <th className="text-right py-2">Close UPI</th>
                <th className="text-right py-2">Diff</th>
                <th className="text-left py-2">Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {days.length === 0 ? (
                <tr><td colSpan={13} className="py-8 text-center text-brand-charcoal/50">No records yet.</td></tr>
              ) : days.map((d) => (
                <tr key={d.date} className="border-t border-brand-line hover:bg-brand-muted/30 cursor-pointer" onClick={() => drilldown(d.date)} data-testid={`cash-day-${d.date}`}>
                  <td className="py-2 font-mono">{d.date} <ChevronRight size={10} className="inline text-brand-charcoal/40" /></td>
                  <td className="py-2 text-right font-mono">{fmt(d.opening_cash)}</td>
                  <td className="py-2 text-right font-mono">{fmt(d.cash_sales)}</td>
                  <td className="py-2 text-right font-mono text-red-700">−{fmt(d.cash_expenses)}</td>
                  <td className="py-2 text-right font-mono text-red-700">−{fmt(d.cash_withdrawals)}</td>
                  <td className="py-2 text-right font-mono font-medium">{fmt(d.physical_cash || d.expected_closing_cash)}</td>
                  <td className="py-2 text-right font-mono">{fmt(d.opening_upi)}</td>
                  <td className="py-2 text-right font-mono">{fmt(d.upi_sales)}</td>
                  <td className="py-2 text-right font-mono text-red-700">−{fmt(Number(d.upi_expenses || 0) + Number(d.upi_purchases || 0))}</td>
                  <td className="py-2 text-right font-mono font-medium">{fmt(d.closing_upi)}</td>
                  <td className={`py-2 text-right font-mono ${Math.abs(Number(d.cash_difference || 0)) < 0.01 ? "text-emerald-700" : d.cash_difference < 0 ? "text-red-700" : "text-amber-700"}`}>{fmt(d.cash_difference)}</td>
                  <td className="py-2">{d.status === "closed" ? <span className="text-[10px] uppercase tracking-widest bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full inline-flex items-center gap-1"><Lock size={9} /> Closed</span> : <span className="text-[10px] uppercase tracking-widest bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full inline-flex items-center gap-1"><CheckCircle2 size={9} /> Open</span>}</td>
                  <td className="py-2 text-right">{d.status === "closed" && <button onClick={(e) => { e.stopPropagation(); reopen(d.date); }} className="text-xs text-brand-charcoal hover:bg-brand-muted rounded px-2 py-0.5 inline-flex items-center gap-1" data-testid={`cash-reopen-${d.date}`}><Unlock size={10} /> Reopen</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ---------- Detailed per-day cards ---------- */}
      <section className="space-y-4" data-testid="ledger-cards">
        <div className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Full daily breakdown</div>
        {days.map((d) => {
          const isOpen = !!expanded[d.date];
          const diff = Number(d.cash_difference || 0);
          const matched = Math.abs(diff) < 0.01;
          return (
            <div key={d.date} className="rounded-2xl bg-white border border-brand-line p-5" data-testid={`day-card-${d.date}`}>
              <header className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <div>
                  <div className="font-display text-xl">{d.date}</div>
                  <div className={`text-[11px] mt-1 ${matched ? "text-emerald-700" : diff < 0 ? "text-red-700" : "text-amber-700"}`}>
                    {matched ? "🟢 MATCHED" : diff < 0 ? `🔴 SHORTAGE ${fmt(Math.abs(diff))}` : `🟠 EXCESS ${fmt(diff)}`}
                  </div>
                </div>
                <div className="flex gap-2">
                  {master && overrideMode && (
                    <button onClick={() => setEditDay(d.date)} className="btn-pill btn-ghost text-xs border-amber-300 text-amber-700" data-testid={`edit-day-${d.date}`}>
                      <Pencil size={11} /> Edit
                    </button>
                  )}
                  <button onClick={() => drilldown(d.date)} className="btn-pill btn-ghost text-xs" data-testid={`card-details-${d.date}`}><Clock size={11} /> Timeline</button>
                  <button onClick={() => setExpanded((s) => ({ ...s, [d.date]: !isOpen }))} className="btn-pill btn-ghost text-xs">{isOpen ? <ChevronUp size={11} /> : <ChevronDown size={11} />} {isOpen ? "Collapse" : "Expand"}</button>
                </div>
              </header>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                <Kv k="OPENING" c="Cash" v={d.opening_cash} />
                <Kv k=" " c="UPI" v={d.opening_upi} />
                <Kv k="SALES" c="Billing" v={d.total_billing} />
                <Kv k=" " c="Cash / UPI" v={`${fmt(d.cash_sales)} / ${fmt(d.upi_sales)}`} raw />
              </div>
              {isOpen && (
                <div className="mt-4 pt-4 border-t border-brand-line grid md:grid-cols-2 gap-4">
                  <div className="rounded-xl bg-brand-muted/25 border border-brand-line p-3">
                    <div className="text-[10px] uppercase tracking-widest text-brand-charcoal/55 mb-2">Cash Movement</div>
                    <Ledger rows={[
                      ["Opening Cash", d.opening_cash, "="],
                      ["+ Cash Sales", d.cash_sales, "+"],
                      ["+ Cash Added", d.cash_additions, "+"],
                      ["− Cash Expenses", d.cash_expenses, "−"],
                      ["− Owner Cash Removed", d.cash_withdrawals, "−"],
                    ]} total={["Closing Cash", d.physical_cash || d.expected_closing_cash]} />
                  </div>
                  <div className="rounded-xl bg-brand-muted/25 border border-brand-line p-3">
                    <div className="text-[10px] uppercase tracking-widest text-brand-charcoal/55 mb-2">UPI Movement</div>
                    <Ledger rows={[
                      ["Opening UPI", d.opening_upi, "="],
                      ["+ UPI Sales", d.upi_sales, "+"],
                      ["+ UPI Additions", d.owner_additions, "+"],
                      ["− UPI Expenses", d.upi_expenses, "−"],
                      ["− UPI Purchases", d.upi_purchases, "−"],
                      ["− UPI to Owner", d.owner_transfers, "−"],
                    ]} total={["Closing UPI", d.closing_upi]} />
                  </div>
                  <div className="rounded-xl bg-brand-muted/25 border border-brand-line p-3 md:col-span-2">
                    <div className="text-[10px] uppercase tracking-widest text-brand-charcoal/55 mb-2">Reconciliation</div>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <Kv k="Expected Cash" v={d.expected_closing_cash} />
                      <Kv k="Physical Cash" v={d.physical_cash} />
                      <Kv k="Difference" v={d.cash_difference} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </section>

      {/* ---------- Drilldown modal (with timeline) ---------- */}
      {openDay && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={() => setOpenDay(null)}>
          <div className="max-w-4xl w-full bg-white rounded-2xl p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="cash-day-modal">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Daily snapshot & money movement</div>
                <div className="font-display text-2xl">{openDay.date}</div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => recompute(openDay.date)} className="btn-pill btn-ghost text-xs"><RefreshCcw size={11} /> Re-carry openings</button>
                <button onClick={() => setOpenDay(null)} className="text-brand-charcoal/60 hover:text-brand-charcoal"><X size={18} /></button>
              </div>
            </div>
            {openDay.loading ? <div className="text-center text-brand-charcoal/50 py-8">Loading…</div> : (
              <>
                <div className="text-[11px] uppercase tracking-widest text-brand-charcoal/55 mb-2">Money movement (chronological)</div>
                <ol className="relative border-l border-brand-line pl-4 space-y-2">
                  <TLine time={null} label="Opening Cash" v={openDay.data.opening_cash} sign="=" />
                  <TLine time={null} label="Opening UPI" v={openDay.data.opening_upi} sign="=" />
                  {(openDay.data.transactions || []).map((t) => {
                    const isMinus = ["cash_expense", "cash_withdrawal", "upi_expense", "upi_purchase", "upi_transfer"].includes(t.type);
                    return (
                      <TLine
                        key={t.id}
                        time={t.created_at}
                        label={`${TX_LABEL[t.type] || t.type}${t.category ? " — " + t.category : ""}${t.note ? ` (${t.note})` : ""}`}
                        v={t.amount}
                        sign={isMinus ? "−" : "+"}
                        action={overrideMode ? (
                          <button onClick={() => setDeletingTx(t)} className="text-red-600 hover:bg-red-50 rounded p-0.5" data-testid={`modal-tx-del-${t.id}`}><Trash size={12} /></button>
                        ) : null}
                      />
                    );
                  })}
                  {Number(openDay.data.total_billing) > 0 && (
                    <TLine time={null} label={`Total Billing entered — cash ${fmt(openDay.data.cash_sales)}, UPI ${fmt(openDay.data.upi_sales)}`} v={openDay.data.total_billing} sign="=" />
                  )}
                </ol>
                {openDay.data.status === "closed" && (
                  <div className="mt-3 text-[11px] text-brand-charcoal/60">Closed at {openDay.data.closed_at ? new Date(openDay.data.closed_at).toLocaleString() : "?"} by <strong>{openDay.data.closed_by}</strong>{openDay.data.reopened_at && <> · Reopened at {new Date(openDay.data.reopened_at).toLocaleString()} by <strong>{openDay.data.reopened_by}</strong></>}</div>
                )}
              </>
            )}
          </div>
        </div>
      )}
      {/* ---------- OVERRIDE PASSWORD MODAL ---------- */}
      {showOverridePrompt && (
        <OverridePrompt
          onUnlock={() => { setOverrideMode(true); setShowOverridePrompt(false); toast.success("Override mode enabled — you can now edit and delete on any day"); }}
          onClose={() => setShowOverridePrompt(false)}
        />
      )}

      {/* ---------- EDIT DAY MODAL ---------- */}
      {editDay && (
        <EditDayModal
          date={editDay}
          onClose={() => setEditDay(null)}
          onDone={() => { setEditDay(null); load(); if (openDay?.date === editDay) drilldown(editDay); }}
        />
      )}

      {/* ---------- DELETE TRANSACTION CONFIRM ---------- */}
      {deletingTx && (
        <div className="fixed inset-0 z-[60] bg-black/60 grid place-items-center p-4" onClick={() => setDeletingTx(null)}>
          <div className="max-w-sm w-full bg-white rounded-2xl p-6" onClick={(e) => e.stopPropagation()} data-testid="delete-tx-modal">
            <div className="font-display text-xl mb-3">Delete transaction?</div>
            <p className="text-sm text-brand-charcoal/70 mb-4">
              <strong>{TX_LABEL[deletingTx.type] || deletingTx.type}</strong> — ₹{Number(deletingTx.amount).toFixed(2)}
              {deletingTx.note && <> · {deletingTx.note}</>}
              <br />by {deletingTx.created_by} at {new Date(deletingTx.created_at).toLocaleTimeString()}
            </p>
            <p className="text-xs text-red-600 mb-4">This will permanently remove this entry and recalculate all following days.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeletingTx(null)} className="btn-pill btn-ghost text-xs">Cancel</button>
              <button
                onClick={async () => {
                  try {
                    await api.delete(`/admin/cash/transactions/${deletingTx.id}`);
                    toast.success("Deleted — all following days recalculated");
                    setDeletingTx(null);
                    load();
                    if (openDay?.date) drilldown(openDay.date);
                  } catch (e) { toast.error(formatApiError(e)); }
                }}
                className="btn-pill text-xs bg-red-600 text-white hover:bg-red-700"
                data-testid="delete-tx-confirm"
              >Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
function Stat({ label, value, accent }) {
  return <div className={`rounded-2xl border p-4 ${accent ? "bg-amber-50 border-amber-200" : "bg-white border-brand-line"}`}><div className="text-[10px] uppercase tracking-widest text-brand-charcoal/55">{label}</div><div className="text-2xl font-display mt-1">{value}</div></div>;
}
function Hp({ ok, label }) { return <div className="inline-flex items-center gap-1.5">{ok ? <span className="text-emerald-600">🟢</span> : <span className="text-red-600">🔴</span>} {label}</div>; }
function Kv({ k, c, v, raw }) {
  return (
    <div className="rounded-xl border border-brand-line px-3 py-2 min-w-0">
      {k?.trim() && <div className="text-[9px] uppercase tracking-widest text-brand-gold">{k}</div>}
      {c && <div className="text-[10px] text-brand-charcoal/55">{c}</div>}
      <div className="font-mono text-sm truncate">{raw ? v : fmt(v)}</div>
    </div>
  );
}
function Ledger({ rows, total }) {
  return (
    <>
      <ul className="space-y-1 text-sm">
        {rows.map(([label, val, sign], i) => (
          <li key={i} className="flex items-center justify-between border-b border-brand-line/60 py-1">
            <span className="text-brand-charcoal/75">{label}</span>
            <span className="font-mono">{sign === "−" ? "−" : ""}{fmt(val)}</span>
          </li>
        ))}
      </ul>
      <div className="mt-2 flex items-center justify-between font-medium">
        <span>{total[0]}</span>
        <span className="font-mono text-lg">{fmt(total[1])}</span>
      </div>
    </>
  );
}
function TLine({ time, label, v, sign, action }) {
  return (
    <li className="text-sm">
      <div className="text-[11px] text-brand-charcoal/55">{time ? new Date(time).toLocaleTimeString() : "—"}</div>
      <div className="flex items-center justify-between gap-2">
        <span className="truncate">{label}</span>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`font-mono ${sign === "−" ? "text-red-700" : sign === "+" ? "text-emerald-700" : "text-brand-charcoal/70"}`}>{sign !== "=" ? sign : ""}{fmt(v)}</span>
          {action}
        </div>
      </div>
    </li>
  );
}

/* ---------- OVERRIDE PASSWORD PROMPT ---------- */
function OverridePrompt({ onUnlock, onClose }) {
  const [password, setPassword] = useState("");
  const [checking, setChecking] = useState(false);
  const submit = async () => {
    if (!password) return toast.error("Enter password");
    setChecking(true);
    try {
      await api.post("/admin/cash/override/verify", { password });
      onUnlock();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setChecking(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={onClose}>
      <div className="max-w-sm w-full bg-white rounded-2xl p-6" onClick={(e) => e.stopPropagation()} data-testid="override-modal">
        <div className="flex items-center gap-2 mb-3"><KeyRound size={18} className="text-amber-600" /><div className="font-display text-xl">Master Override</div></div>
        <p className="text-sm text-brand-charcoal/70 mb-4">Re-enter your Master password to enable editing and deleting on any of the last 7 days.</p>
        <input
          type="password" value={password} onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          placeholder="Master password" className="pp-input mb-4" data-testid="override-password" autoFocus
        />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="btn-pill btn-ghost text-xs">Cancel</button>
          <button onClick={submit} disabled={checking} className="btn-pill btn-dark text-xs" data-testid="override-submit">{checking ? "Checking…" : "Unlock"}</button>
        </div>
      </div>
    </div>
  );
}

/* ---------- EDIT DAY MODAL (override mode) ---------- */
function EditDayModal({ date, onClose, onDone }) {
  const [day, setDay] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/admin/cash/day/${date}`); setDay(data); }
      catch (e) { toast.error(formatApiError(e)); onClose(); }
    })();
    // eslint-disable-next-line
  }, [date]);

  if (!day) return null;
  const locked = day.status === "closed";

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/cash/day/${date}`, {
        opening_cash: parseFloat(day.opening_cash || 0),
        opening_upi: parseFloat(day.opening_upi || 0),
        total_billing: parseFloat(day.total_billing || 0),
        upi_sales: parseFloat(day.upi_sales || 0),
        denominations: day.denominations,
        coins_extra: parseFloat(day.coins_extra || 0),
      });
      toast.success("Day updated — all following days recalculated");
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const DENOM_KEYS = [2000, 500, 200, 100, 50, 20, 10, 5, 2, 1];
  return (
    <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={onClose}>
      <div className="max-w-lg w-full bg-white rounded-2xl p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="edit-day-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Override — Edit Day</div>
            <div className="font-display text-2xl">{date}</div>
            {locked && <span className="text-[10px] uppercase tracking-widest bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full mt-1 inline-flex items-center gap-1"><Lock size={9} /> Closed</span>}
          </div>
          <button onClick={onClose} className="text-brand-charcoal/60 hover:text-brand-charcoal"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Opening Cash (₹)"><input type="number" step="0.01" value={day.opening_cash ?? 0} onChange={(e) => setDay({ ...day, opening_cash: e.target.value })} className="pp-input" data-testid="edit-opening-cash" /></Field>
            <Field label="Opening UPI (₹)"><input type="number" step="0.01" value={day.opening_upi ?? 0} onChange={(e) => setDay({ ...day, opening_upi: e.target.value })} className="pp-input" data-testid="edit-opening-upi" /></Field>
            <Field label="Total Billing (₹)"><input type="number" step="0.01" value={day.total_billing ?? 0} onChange={(e) => setDay({ ...day, total_billing: e.target.value })} className="pp-input" data-testid="edit-total-billing" /></Field>
            <Field label="UPI Sales (₹)"><input type="number" step="0.01" value={day.upi_sales ?? 0} onChange={(e) => setDay({ ...day, upi_sales: e.target.value })} className="pp-input" data-testid="edit-upi-sales" /></Field>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-brand-charcoal/55 mb-2">Denominations</div>
            <div className="grid grid-cols-5 gap-2">
              {DENOM_KEYS.map((d) => (
                <div key={d}>
                  <label className="text-[9px] text-brand-charcoal/50">₹{d}</label>
                  <input type="number" min="0" value={day.denominations?.[String(d)] ?? 0} onChange={(e) => setDay({ ...day, denominations: { ...(day.denominations || {}), [String(d)]: parseInt(e.target.value || 0) } })} className="pp-input text-center font-mono text-xs" data-testid={`edit-denom-${d}`} />
                </div>
              ))}
            </div>
            <div className="mt-2">
              <Field label="Coins (₹ total)"><input type="number" min="0" step="0.5" value={day.coins_extra ?? 0} onChange={(e) => setDay({ ...day, coins_extra: e.target.value })} className="pp-input" data-testid="edit-coins" /></Field>
            </div>
          </div>
          <div className="pt-2 flex justify-end gap-2">
            <button onClick={onClose} className="btn-pill btn-ghost text-xs">Cancel</button>
            <button onClick={save} disabled={saving} className="btn-pill btn-dark text-xs" data-testid="edit-day-save">{saving ? "Saving…" : "Save & Recalculate"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
function Field({ label, children }) {
  return (<div><label className="text-[10px] uppercase tracking-widest text-brand-charcoal/55 block mb-1">{label}</label>{children}</div>);
}
